import { useState, useEffect, useCallback, useRef } from 'react';
import { TrainingDataSplitData, CalculatedCounts, UseDataSplitReturn } from '../types';

interface UseDataSplitProps {
  totalDataPoints: number;
  onSplitChange: (split: TrainingDataSplitData) => void;
}

/**
 * Custom hook for managing training data split state and calculations
 */
export const useDataSplit = ({
  totalDataPoints,
  onSplitChange
}: UseDataSplitProps): UseDataSplitReturn => {
  const [trainPercentage, setTrainPercentage] = useState(70);
  const [valPercentage, setValPercentage] = useState(20);
  const [testPercentage, setTestPercentage] = useState(10);
  const [randomDat, setRandomDat] = useState(false);

  // Store calculated counts to avoid duplicate calculations
  const [calculatedCounts, setCalculatedCounts] = useState<CalculatedCounts>({
    n_train: 0,
    n_val: 0,
    n_test: 0
  });

  // Use ref to track onSplitChange to avoid re-triggering useEffect
  const onSplitChangeRef = useRef(onSplitChange);

  useEffect(() => {
    onSplitChangeRef.current = onSplitChange;
  }, [onSplitChange]);

  // Calculate actual counts based on percentages and total data points
  const calculateSplit = useCallback((trainPct: number, valPct: number, nDat: number): CalculatedCounts => {
    // Following the exact logic from Python backend:
    // n_train = round(0.7*n_dat)
    // n_val = round(0.2*n_dat)
    // n_tst = n_dat-n_train-n_val

    const n_train = Math.round((trainPct / 100) * nDat);
    const n_val = Math.round((valPct / 100) * nDat);
    // Prevent negative n_test
    const n_test = Math.max(0, nDat - n_train - n_val);

    return { n_train, n_val, n_test };
  }, []);

  // Update calculations when percentages or total data points change
  useEffect(() => {
    if (totalDataPoints > 0) {
      const counts = calculateSplit(
        trainPercentage,
        valPercentage,
        totalDataPoints
      );

      setCalculatedCounts(counts);

      const splitData: TrainingDataSplitData = {
        ...counts,
        random_dat: randomDat,
        trainPercentage,
        valPercentage,
        testPercentage
      };

      onSplitChangeRef.current(splitData);
    }
  }, [trainPercentage, valPercentage, testPercentage, randomDat, totalDataPoints, calculateSplit]);

  // Handle percentage changes and auto-adjust the third percentage
  const handleTrainPercentageChange = useCallback((value: number) => {
    // Validate input - handle NaN
    if (isNaN(value)) return;

    const newVal = Math.max(0, Math.min(100, value));
    setTrainPercentage(newVal);

    // Auto-adjust test percentage to maintain 100% total
    const remaining = 100 - newVal - valPercentage;
    if (remaining >= 0) {
      setTestPercentage(remaining);
    } else {
      // If not enough remaining, adjust val percentage
      const newValPct = Math.max(0, 100 - newVal - 1); // Keep at least 1% for test
      setValPercentage(newValPct);
      setTestPercentage(100 - newVal - newValPct);
    }
  }, [valPercentage]);

  const handleValPercentageChange = useCallback((value: number) => {
    // Validate input - handle NaN
    if (isNaN(value)) return;

    const newVal = Math.max(0, Math.min(100, value));
    setValPercentage(newVal);

    // Auto-adjust test percentage to maintain 100% total
    const remaining = 100 - trainPercentage - newVal;
    if (remaining >= 0) {
      setTestPercentage(remaining);
    } else {
      // If not enough remaining, adjust train percentage
      const newTrainPct = Math.max(0, 100 - newVal - 1); // Keep at least 1% for test
      setTrainPercentage(newTrainPct);
      setTestPercentage(100 - newTrainPct - newVal);
    }
  }, [trainPercentage]);

  // Check if percentages sum exceeds 100% for visual feedback
  const totalPercentage = trainPercentage + valPercentage + testPercentage;
  const isInvalidSum = totalPercentage > 100.1; // Small tolerance for floating point

  // Check if train or validate are empty (0 or less)
  const isTrainEmpty = trainPercentage <= 0;
  const isValEmpty = valPercentage <= 0;

  return {
    trainPercentage,
    valPercentage,
    testPercentage,
    randomDat,
    calculatedCounts,
    isInvalidSum,
    isTrainEmpty,
    isValEmpty,
    totalPercentage,
    handleTrainPercentageChange,
    handleValPercentageChange,
    setRandomDat
  };
};

export default useDataSplit;
